import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { ProductConfig } from 'app/product-designer-config';
import { ProductDesignerService } from 'app/product-designer.service';
import { ToolboxService } from 'app/toolboxes/toolbox.service';
import { CollageDesignerConfig
       } from 'collage-designer/collage-designer-config';
import { CollageDesignerConfigParser
       } from 'collage-designer/collage-designer-config-parser';
import { ConfigParserError } from 'core/config/config-parser';
import { CriticalError } from 'core/error/error';
import { ErrorService } from 'core/error/error.service';
import { ImageStoreService } from 'core/imagestore/imagestore.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitter } from 'core/utils/async-event-emitter';

export class ProductServiceError extends CriticalError
{
  protected _name: string = 'ProductServiceError';
}

@Injectable()
export class ProductService
{
  private http: HttpClient;
  private errorService: ErrorService;
  private toolboxService: ToolboxService;

  private productConfigs: {[productId: string]: ProductConfig} = {};

  public productHasChanged: AsyncEventEmitter<ProductConfig>
      = new AsyncEventEmitter();

  private products: Array<ProductConfig>;
  private currentProduct: ProductConfig;

  constructor(
      @Inject(HttpClient) http: HttpClient,
      @Inject(ErrorService) errorService: ErrorService,
      @Inject(ToolboxService) toolboxService: ToolboxService,
      @Inject(ProductDesignerService) private productDesignerService:
          ProductDesignerService,
      @Inject(ImageStoreService) private imageStoreService: ImageStoreService
  )
  {
    this.http = http;
    this.errorService = errorService;
    this.toolboxService = toolboxService;
  }

  public async setProducts(
      products: Array<ProductConfig>, selectedProduct?: string): Promise<void>
  {
    this.products = products;

    if (!selectedProduct) {
      selectedProduct = this.products[0].id;
    }
    await this.setCurrentProduct(selectedProduct);
  }

  public getProducts(): Array<ProductConfig>
  {
    return this.products;
  }

  public async setCurrentProduct(productConfigId): Promise<void>
  {
    this.productDesignerService.suspendDesignerTrigger.emit({suspend: true});
    this.toolboxService.closeActiveToolbox();

    if (this.currentProduct) {
      this.updateProductConfig(this.currentProduct);
    }

    this.currentProduct = await this.getProductConfigById(productConfigId);
    await this.productHasChanged.emit(this.currentProduct);
  }

  public getCurrentProduct(): ProductConfig
  {
    return this.currentProduct;
  }

  public updateProductConfig(productConfig: ProductConfig): void
  {
    this.productConfigs[productConfig.id] = productConfig;
  }

  public async getProductConfigById(productId: string): Promise<ProductConfig>
  {
    let productConfig = this.productConfigs[productId];
    if (!productConfig.collageDesignerConfig) {
      let collageDesignerConfigURL = productConfig.collageDesignerConfigURL;
      productConfig.collageDesignerConfig =
          await this.resolveCollageDesignerConfig(
              collageDesignerConfigURL, productId);
    }
    return productConfig;
  }

  /**
   * @throws CriticalError
   **/
  private async resolveCollageDesignerConfig(configURL: string,
      productId: string): Promise<CollageDesignerConfig>
  {
    try {
      const resp = <any> await this.http.get(configURL,
          { observe: 'response', responseType: 'json' })
        .pipe(Utils.retryPipe())
        .toPromise()
        .catch((exc) => {
          throw exc;
        });

      const configParser = new CollageDesignerConfigParser(
          resp.body, productId, this.imageStoreService);

      try {
        return await configParser.getConfig();
      }
      catch (error) {
        if (!(error instanceof ConfigParserError)) {
          error = new ProductServiceError(
              'parsing collagedesigner-config failed');
        }
        this.errorService.add(error, 'ProductService');
        this.errorService.flush('ProductService');
        throw error;
      }
    } catch (error) {
      if (!error.url) {
        error.url = configURL;
      }
      this.errorService.catchHttpOrSyntaxError(error, 'collagedesigner-config');
    }
  }
}
